// Some common actions

import type { ReduxActionType } from "../flow/ReduxActionType";
import { select } from "redux-saga/effects";
import cookieToken from "../helpers/cookieToken";
import { isDispatchDomain } from "../helpers/domains";

export function *isPluginMode() {
    // dispatchedDomain is either null or a URL string
    const data = yield select(state => state.App.dispatchedDomain);
    if (typeof data !== 'string') {
        return false;
    }

    // Empty string is a no-no
    if (data.trim().length === 0) {
        return false;
    }

    // Check if the URL is valid
    try {
        new URL(data);
        return true;
    } catch (e) {
        // Failed to parse the URL
        return false;
    }
}

export function *getPluginModeUrl() {
    const data = yield select(state => state.App.dispatchedDomain);
    if (typeof data !== 'string') {
        return null;
    }

    // Empty string is a no-no
    if (data.trim().length === 0) {
        return null;
    }

    // Check if the URL is valid
    try {
        const parsed = new URL(data);
        return parsed.href;
    } catch (e) {
        // Failed to parse the URL
        return null;
    }
}

export function *getPluginToken() {
    const data = yield select(state => state.Auth.plugin_token);
    if (data) {
        return data;
    }
    return null;
}

/**
 * Check if the current window is a plugin mode window
 * @returns {boolean}
 */
export function isPluginModeWindow() {
    // This is checking from the window object for
    window.__linguisePluginModeWindow = window.__linguisePluginModeWindow || false;
    return window.__linguisePluginModeWindow;
}

export function getPluginTokenFromWindow() {
    // This is checking from the window object for
    window.__linguisePluginModeWindowToken = window.__linguisePluginModeWindowToken || null;
    return window.__linguisePluginModeWindowToken;
}

export const createPluginModeWindowMiddleware = () => ({dispatch, getState}) => (next) => (action: ReduxActionType) => {
    const { type, payload } = action;

    if (type === 'APP_SET_DISPATCHED_DOMAIN') {
        // Check if the payload is a string and not empty
        if (typeof payload === 'string' && payload.trim().length > 0) {
            // Check if the URL is valid
            try {
                new URL(payload);
                window.__linguisePluginModeWindow = true;
            } catch (e) {
                window.__linguisePluginModeWindow = false;
            }
        } else {
            window.__linguisePluginModeWindow = false;
        }
    }
    if (type === 'AUTH_SET_PLUGIN_TOKEN') {
        if (typeof payload === 'string' && payload.trim().length > 0) {
            cookieToken.setPluginToken(payload);
            // also set to window object
            window.__linguisePluginModeWindowToken = payload;
        }
    }

    const dispatchedDomain = getState().App.dispatchedDomain;

    if (isDispatchDomain(dispatchedDomain)) {
        switch (type) {
            case 'LOGIN_USER_SUCCESS':
            case 'REGISTER_USER_SUCCESS': {
                // Dispatch back token to the plugin mode window
                const dispatchedToken = getState().Auth.plugin_token;
                const token = cookieToken.getPluginToken();
                if (token || dispatchedToken) {
                    window.parent.postMessage(
                        JSON.stringify({
                            t: 'pluginLoginToken',
                            d: token || dispatchedToken,
                        }),
                        dispatchedDomain
                    );
                }
                break;
            }
            default: {
                break;
            }
        }
    }

    return next(action);
};
